import * as React from "react"
import { ChangeEvent, ChangeEventHandler, createContext, useEffect } from "react";
import styled from "styled-components";
import cn from "classnames";

import { useRenderForcingRef } from "../../hooks/useRenderForcingRef";
import { StyledFormControl, StyledFormControlGroup, StyledFormControlLabel } from "../../FormControl/FormControl";
import { ValueOf } from "../../types";

export const RadioGroupContext = createContext<{
  name: string
  disabled: boolean
  onChange: ChangeEventHandler<HTMLInputElement>
  value?: string;
} | null>(null);

const StyledRadioGroup = styled(StyledFormControlGroup)`
  ${StyledFormControlLabel} {
    margin-bottom: 0;
  }

  ${StyledFormControl} {
    margin-bottom: 10px;
  }

  &.inline {
    display: flex;

    &.inline-default {
      gap: 8px;
    }

    &.inline-small {
      gap: 5px;
    }

    &.inline-medium {
      gap: 10px;
    }

    &.inline-large {
      gap: 15px;
    }
  }
`;
StyledRadioGroup.displayName = "StyledRadioGroup";

interface IStyledRadioGroupProps {
  inline?: boolean | "default" | "small" | "medium" | "large";
}

interface IRadioGroupProps<T> extends IStyledRadioGroupProps {
  name: string;
  children: React.ReactNode;
  value: ValueOf<T>;
  disabled?: boolean;
  required?: boolean;
  onChange?: (selected: ValueOf<T>, e?: ChangeEvent<HTMLInputElement>) => void;
}

export function RadioGroup<T>({
                                disabled,
                                name,
                                onChange: onChangeProp,
                                children,
                                value,
                                inline: inlineProp
                              }: IRadioGroupProps<T>): JSX.Element {
  const [selectedRadioValue, setSelectedRadioValue] = useRenderForcingRef<ValueOf<T>>();

  useEffect(() => {
    setSelectedRadioValue(value as unknown as ValueOf<T>);
    if (!onChangeProp) {
      return;
    }
    onChangeProp(value as unknown as ValueOf<T>, undefined);
  }, [value]);

  function updateSelectedCheckboxes(e: ChangeEvent<HTMLInputElement>): void {
    const { value, checked } = e.currentTarget;

    if (checked) {
      setSelectedRadioValue(value as unknown as ValueOf<T>);
    }
  }

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (!onChangeProp) {
      return;
    }

    updateSelectedCheckboxes(e);
    onChangeProp(selectedRadioValue.current as ValueOf<T>, e);
  }

  const inline = inlineProp && typeof inlineProp === "boolean" ? "default" : inlineProp;

  return <StyledRadioGroup className={cn({
    "inline": inline,
    [`inline-${inline}`]: inline
  })}>
    <RadioGroupContext.Provider
      value={{
        disabled: disabled || false,
        name,
        onChange,
        value: value as any
      }}
    >
      {children}
    </RadioGroupContext.Provider>
  </StyledRadioGroup>;
}